import { useEffect, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import { useLocalParticipant } from "@livekit/components-react";
import { Track } from "livekit-client";
import { Video, VideoOff, SwitchCamera } from "lucide-react";

/**
 * HostCamControls — Slice B of the Live UI Control-Clutter Cleanup.
 *
 * Portals the host's Camera on/off and Camera flip controls into the
 * Creator Tools wrench menu (slot `#live-host-cam-slot`). Mirrors the
 * pattern established by `HostTopBarMic` in Slice A so that LiveKit's
 * `useLocalParticipant()` context stays valid while the visual home of
 * the controls lives outside the LiveKitRoom subtree.
 *
 * Behavior kept identical to `LiveRoomAvControls` (which is now hidden
 * for hosts entirely — cohosts still see the dock):
 *   - `setCameraEnabled(!isCameraEnabled)` toggle (optimistic UI via
 *     LiveKit's react hook)
 *   - `restartTrack({ facingMode })` flip; falls back to disable/enable
 *     with the new facing hint on browsers that reject restartTrack
 *   - Silently no-ops on single-camera desktops
 *
 * Mounts only when `canPublish && isHost`. The slot must exist in the
 * open wrench sheet before the buttons appear — the effect polls a
 * couple of animation frames to avoid a first-paint race with Radix's
 * Sheet portal.
 */
export default function HostCamControls({ canPublish, isHost }) {
  const { localParticipant, isCameraEnabled } = useLocalParticipant();
  const [slot, setSlot] = useState(null);
  const [facing, setFacing] = useState("user");
  const [flipping, setFlipping] = useState(false);

  useEffect(() => {
    if (!canPublish || !isHost) return undefined;
    let cancelled = false;
    let raf = 0;
    const find = () => {
      if (cancelled) return;
      const node = document.getElementById("live-host-cam-slot");
      if (node) {
        setSlot(node);
      } else {
        raf = requestAnimationFrame(find);
      }
    };
    find();
    return () => {
      cancelled = true;
      if (raf) cancelAnimationFrame(raf);
      setSlot(null);
    };
  }, [canPublish, isHost]);

  const toggleCamera = useCallback(async () => {
    if (!localParticipant) return;
    try {
      await localParticipant.setCameraEnabled(!isCameraEnabled);
    } catch {
      /* silent */
    }
  }, [localParticipant, isCameraEnabled]);

  const flipCamera = useCallback(async () => {
    if (!localParticipant || flipping) return;
    const next = facing === "user" ? "environment" : "user";
    setFlipping(true);
    try {
      const pub = localParticipant.getTrackPublication(Track.Source.Camera);
      const track = pub?.track;
      if (track?.restartTrack) {
        await track.restartTrack({ facingMode: next });
      } else {
        await localParticipant.setCameraEnabled(false);
        await localParticipant.setCameraEnabled(true, { facingMode: next });
      }
      setFacing(next);
    } catch {
      /* single-camera desktop or permission denial — silent */
    } finally {
      setFlipping(false);
    }
  }, [localParticipant, facing, flipping]);

  if (!canPublish || !isHost || !localParticipant || !slot) return null;

  return createPortal(
    <div className="flex items-center gap-2">
      <button
        type="button"
        data-testid="live-host-cam-toggle"
        onClick={toggleCamera}
        aria-pressed={!isCameraEnabled}
        className={`flex flex-col items-center gap-2 p-3 rounded-xl border transition ${
          isCameraEnabled
            ? "bg-white/5 hover:bg-white/10 border-white/10 text-white"
            : "bg-rose-600/20 hover:bg-rose-600/30 border-rose-500/40 text-rose-100"
        }`}
      >
        {isCameraEnabled ? <Video size={20} /> : <VideoOff size={20} />}
        <span className="text-[11px] tracking-wide">
          {isCameraEnabled ? "Camera on" : "Camera off"}
        </span>
      </button>
      <button
        type="button"
        data-testid="live-host-cam-flip"
        onClick={flipCamera}
        disabled={flipping || !isCameraEnabled}
        className="flex flex-col items-center gap-2 p-3 rounded-xl bg-white/5 hover:bg-white/10 border border-white/10 text-white disabled:opacity-40 transition"
      >
        <SwitchCamera size={20} />
        <span className="text-[11px] tracking-wide">Flip</span>
      </button>
    </div>,
    slot,
  );
}
